import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/AdminShell";
import api, { API } from "@/lib/api";
import { Download, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [open, setOpen] = useState(new Set());

  useEffect(() => {
    document.title = "Admin · Audit log";
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      const qs = [];
      if (q) qs.push(`q=${encodeURIComponent(q)}`);
      if (type) qs.push(`action_type=${encodeURIComponent(type)}`);
      const r = await api.get(`/admin/audit?${qs.join("&")}`);
      setRows(r.data);
    }, 250);
    return () => clearTimeout(t);
  }, [q, type]);

  const types = useMemo(
    () => Array.from(new Set(rows.map((r) => r.action_type))).sort(),
    [rows]
  );

  const toggle = (id) => {
    const n = new Set(open);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setOpen(n);
  };

  return (
    <AdminShell>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Audit log
          </div>
          <h1 className="display mt-1 text-3xl font-black tracking-tight">Admin actions</h1>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reason / action…"
            data-testid="audit-search"
            className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs w-64"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="audit-type"
            className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs"
          >
            <option value="">All actions</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <a
            href={`${API}/admin/audit/export.csv`}
            data-testid="audit-export"
            className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 rounded-[3px] hover:bg-zinc-800"
          >
            <Download size={11} /> Export CSV
          </a>
        </div>
      </div>

      <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] divide-y divide-zinc-800" data-testid="audit-table">
        {rows.length === 0 && (
          <div className="p-3 text-xs text-zinc-500">No admin actions yet.</div>
        )}
        {rows.map((r) => (
          <div key={r.audit_id} className="text-xs">
            <button
              onClick={() => toggle(r.audit_id)}
              className="w-full text-left p-2 hover:bg-zinc-900 flex items-center gap-2"
              data-testid={`audit-row-${r.audit_id}`}
            >
              {open.has(r.audit_id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="mono text-[10px] text-zinc-500 w-36 shrink-0">
                {new Date(r.created_at).toLocaleString()}
              </span>
              <span className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-300 w-44 shrink-0 truncate">
                {r.action_type}
              </span>
              <span className="text-zinc-200 flex-1 truncate">{r.reason}</span>
              <span className="mono text-[10px] text-zinc-500 truncate">
                {r.admin_email}
              </span>
              {r.target_email && (
                <Link
                  to={`/admin/users/${r.target_user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="mono text-[10px] text-zinc-400 hover:text-white truncate max-w-[180px]"
                >
                  → {r.target_email}
                </Link>
              )}
              <span className="mono text-[10px] text-zinc-500 w-28 text-right">
                {r.ip_address || ""}
              </span>
            </button>
            {open.has(r.audit_id) && (
              <pre className="p-3 bg-zinc-950/80 border-t border-zinc-800 text-[10px] mono text-zinc-300 overflow-auto">
{JSON.stringify(r.details, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
